import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Separator } from "@/components/ui/separator"
import AppLayout from "@/layouts/AppLayout"
import { Head, useForm, usePage } from "@inertiajs/react"
import { IconArrowLeft } from "@tabler/icons-react"

interface User {
  id: number
  name: string
}

interface CycleData {
  id: number
  user_id: string
  cycle_period: string
  year: number
  start_date: string
  end_date: string
  supervised_by_id: string
  display_name: string
}

interface Props {
  [key: string]: unknown
  cycle: CycleData
  users: User[]
  supervisors: User[]
}

export default function PerformanceReviewCycleEdit() {
  const { cycle, users, supervisors } = usePage<Props>().props

  const { data, setData, patch, processing, errors } = useForm({
    user_id: String(cycle.user_id || ""),
    cycle_period: cycle.cycle_period || "",
    year: String(cycle.year || new Date().getFullYear()),
    start_date: cycle.start_date || "",
    end_date: cycle.end_date || "",
    supervised_by_id: String(cycle.supervised_by_id || ""),
  })

  const allErrors = Object.values(errors)

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    patch(`/performance_review_cycles/${cycle.id}`)
  }

  return (
    <>
      <Head title={`Edit ${cycle.display_name} - Operations Center`} />
      <div className="flex flex-1 flex-col gap-6 p-4 lg:p-6">
        <div className="space-y-4">
          <Breadcrumb>
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbLink href="/">Home</BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbLink href="/performance_review_cycles">Performance Review Cycles</BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbLink href={`/performance_review_cycles/${cycle.id}`}>{cycle.display_name}</BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbPage>Edit</BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>

          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" className="h-8 w-8" asChild>
              <a href={`/performance_review_cycles/${cycle.id}`}>
                <IconArrowLeft className="h-4 w-4" />
              </a>
            </Button>
            <h1 className="text-lg font-semibold tracking-tight">Edit Performance Review Cycle</h1>
          </div>
        </div>

        <div className="grid gap-6 lg:grid-cols-3">
          <div className="lg:col-span-2">
            <form onSubmit={handleSubmit}>
              <div className="rounded-xl border bg-card">
                <div className="p-6">
                  {allErrors.length > 0 && (
                    <div className="mb-6 rounded-lg border border-destructive/20 bg-destructive/5 p-3">
                      <ul className="space-y-1 text-sm text-destructive">
                        {allErrors.map((err, i) => (
                          <li key={i}>{err}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  <div className="space-y-5">
                    <div className="space-y-2">
                      <Label htmlFor="user_id">Employee</Label>
                      <Select value={data.user_id} onValueChange={(v) => setData("user_id", v)}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select employee..." />
                        </SelectTrigger>
                        <SelectContent>
                          {users.map((u) => (
                            <SelectItem key={u.id} value={String(u.id)}>
                              {u.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="cycle_period">Cycle Period</Label>
                        <Select value={data.cycle_period} onValueChange={(v) => setData("cycle_period", v)}>
                          <SelectTrigger>
                            <SelectValue placeholder="Select period..." />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="april">April</SelectItem>
                            <SelectItem value="october">October</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="year">Year</Label>
                        <Input
                          id="year"
                          type="number"
                          min="2021"
                          value={data.year}
                          onChange={(e) => setData("year", e.target.value)}
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="supervised_by_id">Supervisor</Label>
                      <Select value={data.supervised_by_id} onValueChange={(v) => setData("supervised_by_id", v)}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select supervisor..." />
                        </SelectTrigger>
                        <SelectContent>
                          {supervisors.map((u) => (
                            <SelectItem key={u.id} value={String(u.id)}>
                              {u.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="start_date">Start Date</Label>
                        <Input
                          id="start_date"
                          type="date"
                          value={data.start_date}
                          onChange={(e) => setData("start_date", e.target.value)}
                        />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="end_date">End Date</Label>
                        <Input
                          id="end_date"
                          type="date"
                          value={data.end_date}
                          onChange={(e) => setData("end_date", e.target.value)}
                        />
                      </div>
                    </div>
                  </div>
                </div>

                <Separator />
                <div className="flex justify-end gap-2 p-4">
                  <Button variant="ghost" asChild>
                    <a href={`/performance_review_cycles/${cycle.id}`}>Cancel</a>
                  </Button>
                  <Button type="submit" disabled={processing}>
                    {processing ? "Updating..." : "Update Cycle"}
                  </Button>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    </>
  )
}

PerformanceReviewCycleEdit.layout = (page: React.ReactNode) => <AppLayout>{page}</AppLayout>
